import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import TrackingInput from './components/TrackingInput';
import TrackingTimeline from './components/TrackingTimeline';
import ShipmentDetails from './components/ShipmentDetails';
import FooterSection from '../home-landing/components/FooterSection';

const ShipmentTracking = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shipmentData, setShipmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Mock shipment data - in real app this would come from API
  const mockShipmentData = {
    trackingNumber: 'DWS123456789',
    status: 'In Transit',
    currentStage: 2,
    stages: [
      { id: 1, name: 'Package Received', completed: true, date: '2026-01-27 10:30 AM', location: 'New York, NY' },
      { id: 2, name: 'In Transit', completed: true, date: '2026-01-28 02:15 PM', location: 'Philadelphia, PA' },
      { id: 3, name: 'Out for Delivery', completed: false, date: null, location: null },
      { id: 4, name: 'Delivered', completed: false, date: null, location: null }
    ],
    packageDetails: {
      weight: '2.5 kg',
      dimensions: '30 x 20 x 15 cm',
      contents: 'Electronics'
    },
    origin: {
      address: '123 Main St, New York, NY 10001',
      contact: 'John Sender'
    },
    destination: {
      address: '456 Oak Ave, Boston, MA 02101',
      contact: 'Jane Receiver'
    },
    estimatedDelivery: 'January 30, 2026',
    carrier: 'Dream Work Express',
    currentLocation: 'Philadelphia Distribution Center'
  };

  const handleTrackShipment = (number) => {
    setError('');
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      if (number?.length >= 8) {
        setShipmentData({
          ...mockShipmentData,
          trackingNumber: number
        });
        setTrackingNumber(number);
      } else {
        setError('Invalid tracking number. Please enter a valid tracking number.');
        setShipmentData(null);
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleReset = () => {
    setTrackingNumber('');
    setShipmentData(null);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header 
        onCartClick={() => {}}
        onAccountClick={() => {}}
        onLogout={() => {}}
        onSearch={() => {}}
      />
      <main className="flex-1 pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-3">
              Track Your Shipment
            </h1>
            <p className="text-base sm:text-lg font-body text-muted-foreground max-w-2xl mx-auto">
              Enter your tracking number to monitor your package delivery in real-time
            </p>
          </motion.div>

          {/* Tracking Input Section */}
          <TrackingInput
            onTrack={handleTrackShipment}
            isLoading={isLoading}
            error={error}
          />

          {/* Results Section */}
          {shipmentData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6 mt-8"
            >
              {/* Current Status Banner */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-sm font-body text-muted-foreground mb-1">
                      Tracking Number: <span className="font-semibold text-foreground">{shipmentData?.trackingNumber}</span>
                    </p>
                    <p className="text-lg font-heading font-bold text-primary">
                      {shipmentData?.status}
                    </p>
                  </div>
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 text-sm font-body font-medium text-primary hover:text-primary-foreground hover:bg-primary border border-primary rounded-lg transition-all duration-200"
                  >
                    Track Another
                  </button>
                </div>
              </div>

              {/* Tracking Timeline */}
              <TrackingTimeline stages={shipmentData?.stages} currentStage={shipmentData?.currentStage} />

              {/* Shipment Details */}
              <ShipmentDetails shipment={shipmentData} />
            </motion.div>
          )}

          {/* Empty State */}
          {!shipmentData && !error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-center py-12"
            >
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <p className="text-lg font-body text-muted-foreground">
                Enter a tracking number above to view shipment details
              </p>
            </motion.div>
          )}
        </div>
      </main>
      <FooterSection />
    </div>
  );
};

export default ShipmentTracking;